import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import {
  urlApiPaymentMethod,
  urlBackend,
} from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import {
  IPaymentMethod,
  IPaymentMethodDataRes,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchPaymentMethods = (
  page = 1,
  nombre = '',
  page_size = 10
) => {
  return useQuery({
    queryKey: ['paymentMethods', page, nombre, page_size],
    queryFn: async () => {
      return await getPaymentMethods({ page, nombre, page_size });
    },
  });
};

export const useFetchPaymentMethodsParams = (
  params?: getPaymentMethodsProps,
  enabled = true
) => {
  return useQuery({
    queryKey: ['paymentMethods', ...Object.values(params ?? {})],

    queryFn: async () => {
      return await getPaymentMethods(params);
    },

    enabled: enabled,
  });
};


export const useFetchPaymentMethodsParamsProveedor = (
  params?: getPaymentMethodsProps,
  enabled = true
) => {
  return useQuery({
    queryKey: ['formapagoproveedor', ...Object.values(params ?? {})],

    queryFn: async () => {
      return await getPaymentMethodsProveedor(params);
    },

    enabled: enabled,
  });
};

export const useCreatePaymentMethod = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createPaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Metodo pago creado correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdatePaymentMethod = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updatePaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Metodo pago actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemovePaymentMethod = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removePaymentMethod,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['paymentMethods'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

export const useGetPaymentMethod = (id: string) => {
  return useQuery({
    queryKey: ['paymentMethod', id],
    queryFn: async () => {
      return getPaymentMethod(id);
    },
  });
};

///* axios
export type getPaymentMethodsProps = Partial<IPaymentMethod> & {
  page?: number;
  page_size?: number;

  entidad_financiera?: string; // code fk
  filterByState?: boolean;
};

export type CreatePaymentMethodProps = Omit<IPaymentMethod, 'id_forma_pago'>;

export const getPaymentMethods = async (props?: getPaymentMethodsProps) => {
  const stateParams = { ...props };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<IPaymentMethodDataRes>(
    `${urlBackend}api/v1/formapago/?${queryParams}`,
    true
  );
};

export const getPaymentMethodsProveedor = async (props?: getPaymentMethodsProps) => {

  const queryParams = getUrlParamsFetch(props ?? {});

  return get<IPaymentMethodDataRes>(
    `${urlBackend}api/v1/formapagoproveedor/?${queryParams}`,
    true
  );
};

export const getPaymentMethod = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<IPaymentMethod>(`${urlApiPaymentMethod}${id}/`, true);

  res.status === 'error' &&
    setAlert(true, 'Método de pago no encontrad', 'error');

  return res;
};

export const createPaymentMethod = async (data: CreatePaymentMethodProps) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<IPaymentMethod>(urlApiPaymentMethod, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updatePaymentMethod = async ({
  id,
  data,
}: {
  id: string;
  data: CreatePaymentMethodProps;
}) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<IPaymentMethod>(
    `${urlApiPaymentMethod}${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const removePaymentMethod = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<IPaymentMethod>(
    `${urlApiPaymentMethod}${id}/`,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
